import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaComments,
  FaClock,
  FaArrowLeft,
  FaExternalLinkAlt,
  FaPaperPlane,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { chatApi } from '../utils/apiUtils';
import MarkdownRenderer from './MarkdownRenderer';

function AIChats() {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Session list
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState('');

  // Active chat
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingAssistant, setStreamingAssistant] = useState('');
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const streamAbortRef = useRef(null);

  // Redirect unauthenticated users
  useEffect(() => {
    if (!authLoading && !user) navigate('/sign-in');
  }, [authLoading, user, navigate]);

  // Load all sessions on mount
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setSessionsLoading(true);
    (async () => {
      try {
        const { sessions: data } = await chatApi.listSessions();
        if (cancelled) return;
        setSessions(data || []);
        // Auto-select from URL or first session (desktop only)
        if (urlSessionId && data?.some((s) => s.id === urlSessionId)) {
          setActiveSessionId(urlSessionId);
        } else if (data?.length > 0) {
          const isDesktop = window.matchMedia('(min-width: 768px)').matches;
          if (isDesktop) setActiveSessionId(data[0].id);
        }
      } catch (err) {
        if (!cancelled)
          setSessionsError(
            err.response?.data?.message || 'Failed to load chats'
          );
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, urlSessionId]);

  // Load messages when active session changes
  useEffect(() => {
    if (!activeSessionId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setMessagesLoading(true);
    setError('');
    (async () => {
      try {
        const data = await chatApi.listMessages(activeSessionId);
        if (!cancelled) setMessages(data.messages || data || []);
      } catch (err) {
        if (!cancelled)
          setError(
            err.response?.data?.message || 'Failed to load messages'
          );
      } finally {
        if (!cancelled) setMessagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSessionId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingAssistant]);

  // Cleanup streaming on unmount
  useEffect(() => {
    return () => {
      if (streamAbortRef.current) streamAbortRef.current.abort();
    };
  }, []);

  const selectSession = (sessionId) => {
    if (sessionId === activeSessionId) return;
    if (streamAbortRef.current) streamAbortRef.current.abort();
    setActiveSessionId(sessionId);
    setMessages([]);
    setStreamingAssistant('');
    setIsStreaming(false);
    setError('');
    setInput('');
  };

  const handleSend = useCallback(() => {
    const content = input.trim();
    if (!content || !activeSessionId || isStreaming) return;
    setInput('');
    setError('');

    const optimisticId = `temp-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: optimisticId,
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      },
    ]);
    setStreamingAssistant('');
    setIsStreaming(true);

    let assembled = '';
    const controller = chatApi.streamMessage(activeSessionId, content, {
      onChunk: (data) => {
        if (!data?.text) return;
        assembled += data.text;
        setStreamingAssistant(assembled);
      },
      onDone: (data) => {
        setMessages((prev) => [
          ...prev,
          {
            id: data?.messageId || `assistant-${Date.now()}`,
            role: 'assistant',
            content: data?.content || assembled,
            createdAt: new Date().toISOString(),
          },
        ]);
        setStreamingAssistant('');
        setIsStreaming(false);
        // Update sidebar preview
        setSessions((prev) =>
          prev.map((s) =>
            s.id === activeSessionId
              ? {
                  ...s,
                  lastMessagePreview: (data?.content || assembled).slice(
                    0,
                    100
                  ),
                  lastMessageRole: 'assistant',
                  lastMessageAt: new Date().toISOString(),
                  messageCount: (s.messageCount || 0) + 2,
                }
              : s
          )
        );
      },
      onError: (err) => {
        setError(err?.message || 'Chat failed');
        setIsStreaming(false);
      },
    });
    streamAbortRef.current = controller;
  }, [input, activeSessionId, isStreaming]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatRelative = (iso) => {
    if (!iso) return '';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '';
    const diffMs = Date.now() - then.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d`;
    return then.toLocaleDateString();
  };

  const activeSession = sessions.find((s) => s.id === activeSessionId);

  if (authLoading) {
    return (
      <div className="max-w-5xl mx-auto py-16 text-center">
        <div className="w-6 h-6 border-2 border-white/20 border-t-yellow-300 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/30 text-xs">Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 mb-4">
        <Link
          to="/ai"
          className="flex items-center gap-1.5 text-white/50 hover:text-white/90 text-xs transition-colors"
        >
          <FaArrowLeft className="text-[10px]" />
          AI Digest
        </Link>
        <span className="text-white/20 text-xs">/</span>
        <span className="text-white/80 text-xs font-semibold">Chats</span>
      </div>

      {/* Messenger container */}
      <div
        className="bg-white rounded-2xl shadow-glass overflow-hidden border border-gray-100"
        style={{ height: 'calc(100vh - 11rem)' }}
      >
        <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />
        <div className="flex h-[calc(100%-4px)]">
          {/* ── Sidebar ── */}
          <div
            className={`${
              activeSessionId ? 'hidden md:flex' : 'flex'
            } flex-col w-full md:w-80 border-r border-gray-100`}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <FaComments className="text-purple-400 text-xs" />
                Conversations
                {!sessionsLoading && (
                  <span className="text-[10px] text-gray-300 font-normal tabular-nums">
                    ({sessions.length})
                  </span>
                )}
              </h2>
            </div>

            <div className="flex-1 overflow-y-auto">
              {sessionsLoading ? (
                <div className="py-12 text-center">
                  <div className="w-5 h-5 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-[11px] text-gray-400">Loading chats...</p>
                </div>
              ) : sessionsError ? (
                <div className="p-4 text-[11px] text-red-500">
                  {sessionsError}
                </div>
              ) : sessions.length === 0 ? (
                <div className="p-8 text-center">
                  <FaComments className="text-gray-200 text-2xl mx-auto mb-3" />
                  <p className="text-xs text-gray-500 font-semibold mb-1">
                    No chats yet
                  </p>
                  <p className="text-[11px] text-gray-400 mb-3">
                    Open a paper and start a chat to see sessions here.
                  </p>
                  <Link
                    to="/"
                    className="text-[11px] text-purple-600 hover:underline font-semibold"
                  >
                    Browse papers
                  </Link>
                </div>
              ) : (
                sessions.map((s) => {
                  const isActive = s.id === activeSessionId;
                  const paperTitle =
                    s.paper?.title || s.title || 'Untitled paper';
                  const preview =
                    s.lastMessagePreview || 'No messages yet.';
                  const roleLabel =
                    s.lastMessageRole === 'assistant'
                      ? 'AI: '
                      : s.lastMessageRole === 'user'
                      ? 'You: '
                      : '';
                  return (
                    <button
                      key={s.id}
                      onClick={() => selectSession(s.id)}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-all duration-150 ${
                        isActive
                          ? 'bg-purple-50/80 border-l-2 border-l-purple-500'
                          : 'hover:bg-gray-50/80 border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span
                          className={`text-xs font-bold truncate flex-1 ${
                            isActive ? 'text-purple-900' : 'text-gray-900'
                          }`}
                        >
                          {paperTitle}
                        </span>
                        <span className="text-[10px] text-gray-300 flex-shrink-0 tabular-nums flex items-center gap-1">
                          <FaClock className="text-[8px]" />
                          {formatRelative(s.lastMessageAt || s.updatedAt)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        {s.paper?.topic && (
                          <span className="text-[9px] font-bold uppercase tracking-wider text-purple-500">
                            {s.paper.topic}
                          </span>
                        )}
                        <span className="text-[9px] text-gray-300 tabular-nums">
                          {s.messageCount}{' '}
                          {s.messageCount === 1 ? 'msg' : 'msgs'}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">
                        {roleLabel && (
                          <span className="font-semibold text-purple-500">
                            {roleLabel}
                          </span>
                        )}
                        {preview}
                      </p>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Chat area ── */}
          <div
            className={`${
              !activeSessionId ? 'hidden md:flex' : 'flex'
            } flex-col flex-1 min-w-0`}
          >
            {activeSession ? (
              <>
                {/* Chat header */}
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-purple-50/80 to-pink-50/80 flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => setActiveSessionId(null)}
                    className="md:hidden text-gray-400 hover:text-gray-600 p-1 -ml-1"
                  >
                    <FaArrowLeft className="text-xs" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-bold text-gray-900 truncate">
                      {activeSession.paper?.title || 'Untitled paper'}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {activeSession.paper?.topic && (
                        <span className="text-[9px] font-bold uppercase tracking-wider text-purple-500">
                          {activeSession.paper.topic}
                        </span>
                      )}
                      {activeSession.paper?.first_author && (
                        <>
                          <span className="text-gray-200 text-[10px]">
                            ·
                          </span>
                          <span className="text-[10px] text-gray-400 truncate">
                            {activeSession.paper.first_author}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <Link
                    to={`/paper/${activeSession.paperId}`}
                    className="flex items-center gap-1 text-[10px] text-purple-500 hover:text-purple-700 flex-shrink-0 font-semibold"
                  >
                    Paper
                    <FaExternalLinkAlt className="text-[8px]" />
                  </Link>
                </div>

                {/* Messages */}
                <div
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
                >
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="w-5 h-5 border-2 border-gray-200 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
                        <p className="text-[11px] text-gray-400">
                          Loading messages...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {messages.length === 0 && !streamingAssistant && (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-xs text-gray-400 italic">
                            Ask a question about this paper to get started.
                          </p>
                        </div>
                      )}
                      <AnimatePresence initial={false}>
                        {messages.map((m) => (
                          <motion.div
                            key={m.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className={`flex ${
                              m.role === 'user'
                                ? 'justify-end'
                                : 'justify-start'
                            }`}
                          >
                            <div
                              className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed ${
                                m.role === 'user'
                                  ? 'bg-purple-600 text-white rounded-br-sm whitespace-pre-wrap'
                                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                              }`}
                            >
                              {m.role === 'assistant' ? (
                                <MarkdownRenderer content={m.content} compact />
                              ) : (
                                m.content
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      {streamingAssistant && (
                        <div className="flex justify-start">
                          <div className="max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-gray-100 text-gray-800 text-xs leading-relaxed">
                            <MarkdownRenderer content={streamingAssistant} compact />
                            <span className="inline-block w-1.5 h-3 ml-0.5 bg-purple-500 align-middle animate-pulse" />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="px-5 py-2 text-[11px] text-red-500 border-t border-red-50 bg-red-50/50 flex-shrink-0">
                    {error}
                  </div>
                )}

                {/* Input */}
                <div className="px-4 py-3 border-t border-gray-100 flex items-end gap-2 flex-shrink-0">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about the methodology, results, limitations..."
                    rows={1}
                    disabled={isStreaming || messagesLoading}
                    className="flex-1 resize-none rounded-xl border border-gray-200 px-3.5 py-2.5 text-xs text-gray-800 placeholder:text-gray-300 focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100 disabled:bg-gray-50 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={isStreaming || !input.trim() || messagesLoading}
                    className="inline-flex items-center justify-center w-9 h-9 text-white bg-purple-600 rounded-xl disabled:opacity-40 hover:bg-purple-700 transition-colors flex-shrink-0"
                  >
                    {isStreaming ? (
                      <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FaPaperPlane className="text-[11px]" />
                    )}
                  </button>
                </div>
              </>
            ) : (
              /* Empty state — desktop only (mobile shows sidebar instead) */
              <div className="hidden md:flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center mb-4">
                  <FaComments className="text-purple-300 text-lg" />
                </div>
                <p className="text-sm font-semibold text-gray-400 mb-1">
                  Select a conversation
                </p>
                <p className="text-[11px] text-gray-300">
                  Choose a chat from the sidebar to continue
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIChats;
